import React, { useState, useEffect, useRef } from "react";
import { UseIsAdmin } from "../../hooks/auth";
import SoftTypography from "../SoftTypography";
import SoftBox from "../SoftBox";
import SoftButton from "../SoftButton";
import { useTheme } from "@mui/material/styles";
import {
  List,
  ListItemButton,
  ListItemText,
  Popover,
  ListItem,
  ListItemIcon,
} from "@mui/material";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { GET_IMAGE } from "../../helpers/images";
import { toDisplayText } from "../../helpers/errorText";

const PlugPlay = ({
  id,
  url,
  customImage,
  fallbackImageUrl,
  name,
  assignedTo,
  onEdit,
  dragHandleProps,
  isDragging = false,
}) => {
  const appTheme = useTheme();
  const [pluginImage, setPluginImage] = useState(null);
  const isAdmin = UseIsAdmin();

  const {
    primary: { main: primaryColor },
  } = appTheme.palette;

  const HUB_CONTROL_LABELS = {
    EDIT: "Edit Link",
  };

  const [openHubMenu, setOpenHubMenu] = useState(null);
  const ignoreNextCardClickRef = useRef(false);

  const handleOpen = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setOpenHubMenu(event.currentTarget);
  };

  const handleClose = () => {
    ignoreNextCardClickRef.current = true;
    setOpenHubMenu(null);
    window.setTimeout(() => {
      ignoreNextCardClickRef.current = false;
    }, 0);
  };

  const handleLaunch = () => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleCardClick = () => {
    if (ignoreNextCardClickRef.current) return;
    if (openHubMenu) {
      handleClose();
      return;
    }
    handleLaunch();
  };

  const handleCardKeyDown = (event) => {
    if (!url || openHubMenu) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleLaunch();
    }
  };

  const openHubAppMenu = Boolean(openHubMenu);

  useEffect(() => {
    if (customImage && customImage.type) {
      setPluginImage(URL.createObjectURL(customImage));
    } else {
      const image = customImage || fallbackImageUrl;
      if (image) {
        setPluginImage(GET_IMAGE(image));
      }
    }
  }, [customImage, fallbackImageUrl]);

  const assignedCount = assignedTo?.length ?? 0;
  const displayName = toDisplayText(name, "Link");

  return (
    <SoftBox
      onClick={handleCardClick}
      role={url ? "button" : undefined}
      tabIndex={url ? 0 : undefined}
      onKeyDown={handleCardKeyDown}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        borderRadius: "14px",
        border: "1px solid #E4E7EC",
        background: "linear-gradient(180deg, #FFFFFF 0%, #FAFBFC 100%)",
        boxShadow:
          "0px 1px 2px rgba(16, 24, 40, 0.06), 0px 1px 3px rgba(16, 24, 40, 0.1)",
        overflow: "hidden",
        cursor: url ? "pointer" : "default",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease",
        opacity: isDragging ? 0.92 : 1,
        "&:hover": url
          ? {
              borderColor: "#D0D5DD",
              boxShadow:
                "0px 4px 8px rgba(16, 24, 40, 0.08), 0px 12px 24px rgba(16, 24, 40, 0.08)",
              transform: "translateY(-2px)",
            }
          : undefined,
      }}
    >
      <SoftBox
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { mini: "16px", md: "18px" },
          padding: { mini: "16px", md: "20px" },
          paddingTop: { mini: "18px", md: "22px" },
          height: "100%",
        }}
      >
        <SoftBox
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            width: "100%",
            gap: "8px",
          }}
        >
          <SoftBox
            sx={{
              width: { mini: "55px", md: "65px" },
              height: { mini: "55px", md: "65px" },
              flexShrink: 0,
              background: "#fff",
              borderRadius: "12px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: "1px solid #EAECF0",
              boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.06)",
              padding: 0,
              overflow: "hidden",
              "& img": {
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              },
            }}
          >
            {pluginImage ? (
              <img src={pluginImage} alt={displayName} />
            ) : (
              <SoftTypography
                variant="h3"
                sx={{
                  color: "primary.main",
                  fontSize: { mini: "23px", md: "28px" },
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {displayName?.[0]?.toUpperCase()}
              </SoftTypography>
            )}
          </SoftBox>

          <SoftBox
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {dragHandleProps && (
              <SoftBox
                {...dragHandleProps}
                aria-label="Drag to reorder"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "28px",
                  minHeight: "28px",
                  borderRadius: "8px",
                  background: "#F2F4F7",
                  border: "1px solid #EAECF0",
                  cursor: isDragging ? "grabbing" : "grab",
                  color: "#667085",
                  touchAction: "none",
                  "&:hover": {
                    background: "#E9ECEF",
                  },
                }}
              >
                <DragIndicatorIcon sx={{ fontSize: "1.125rem !important" }} />
              </SoftBox>
            )}
            <SoftButton
              sx={{
                boxShadow: "none",
                minWidth: "32px",
                minHeight: "32px",
                width: "32px",
                height: "32px",
                padding: 0,
                borderRadius: "8px",
                background: "#F2F4F7",
                border: "1px solid #EAECF0",
                cursor: "pointer",
                "& svg": {
                  fill: "#667085",
                  fontSize: "1.125rem !important",
                },
                "&:hover": {
                  background: "#E9ECEF",
                },
                "&:focus:not(:hover)": {
                  background: "#F2F4F7",
                },
              }}
              onClick={handleOpen}
            >
              <MoreVertIcon />
            </SoftButton>
          </SoftBox>

          <Popover
            id={`hubAppMenu-${id}`}
            open={openHubAppMenu}
            onClose={handleClose}
            disableRestoreFocus
            anchorEl={openHubMenu}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            PaperProps={{
              sx: {
                borderRadius: "10px",
                padding: "4px",
                background: "white",
                border: "1px solid #EAECF0",
                boxShadow:
                  "0px 4px 6px rgba(16, 24, 40, 0.06), 0px 12px 16px rgba(16, 24, 40, 0.08)",
                minWidth: "140px",
              },
            }}
          >
            <List disablePadding>
              <ListItem disablePadding>
                <ListItemButton
                  sx={{
                    gap: "8px",
                    borderRadius: "8px",
                    py: "8px",
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleClose();
                    onEdit?.(event);
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "max-content" }}>
                    <EditOutlinedIcon sx={{ color: "#344054 !important", fontSize: "1.125rem" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={HUB_CONTROL_LABELS.EDIT}
                    primaryTypographyProps={{
                      color: "#344054",
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </Popover>
        </SoftBox>

        <SoftBox sx={{ flex: 1, minWidth: 0, mt: { mini: "2px", md: "4px" } }}>
          <SoftTypography
            variant="h6"
            sx={{
              fontSize: { mini: "15px", md: "16px" },
              fontWeight: 600,
              color: "#101828",
              lineHeight: 1.45,
              display: "-webkit-box",
              WebkitLineClamp: 4,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              whiteSpace: "normal",
              wordBreak: "break-word",
              marginBottom: assignedCount > 0 && isAdmin ? "8px" : 0,
            }}
          >
            {displayName}
          </SoftTypography>

          {!url && (
            <SoftTypography
              variant="caption"
              sx={{
                display: "inline-block",
                fontSize: "11px",
                fontWeight: 600,
                color: "#B54708",
                background: "#FFFAEB",
                border: "1px solid #FEC84B",
                borderRadius: "999px",
                px: "8px",
                py: "2px",
                mt: assignedCount > 0 && isAdmin ? "6px" : "8px",
              }}
            >
              No URL assigned yet
            </SoftTypography>
          )}

          {isAdmin && assignedCount > 0 && (
            <SoftTypography
              variant="caption"
              sx={{
                display: "inline-block",
                fontSize: "11px",
                fontWeight: 600,
                color: primaryColor,
                background: `${primaryColor}14`,
                borderRadius: "999px",
                px: "8px",
                py: "2px",
              }}
            >
              {assignedCount} player{assignedCount === 1 ? "" : "s"} assigned
            </SoftTypography>
          )}
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
};

export default PlugPlay;
